import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Form, Input, Button, Spin, Alert, InputNumber, Divider, Space, Typography, Tooltip, Radio, Card, Row, Col, Tag } from 'antd';
import { SendOutlined, InfoCircleOutlined, EyeOutlined, EyeInvisibleOutlined, RocketOutlined, ClockCircleOutlined, ReloadOutlined, KeyOutlined } from '@ant-design/icons';
import { useTranslationWithNamespace } from '../../hooks/useTranslationWithNamespace';
import { useSendCrypto } from '../../hooks/useTransactionManagement';
import { showSuccessNotification, showErrorNotification } from '../../utils/notifications';
import { estimateFee, getPrivateKeyByAddress } from '../../api';
import './SendTransactionModal.css';

const { Text } = Typography;

interface SendTransactionModalProps {
  visible: boolean;
  onCancel: () => void;
  walletData: {
    id: string;
    address: string;
    coin: string;
    network: string;
    user_id?: string;
    is_evm?: number;
  };
}

const SendTransactionModal: React.FC<SendTransactionModalProps> = ({
  visible,
  onCancel,
  walletData
}) => {
  const { t } = useTranslationWithNamespace('wallet');
  const [form] = Form.useForm();
  const [privateKeyVisible, setPrivateKeyVisible] = useState(false);
  const [isEstimatingFee, setIsEstimatingFee] = useState(false);
  const [feeEstimation, setFeeEstimation] = useState<any>(null);
  const [feeEstimationError, setFeeEstimationError] = useState<string | null>(null);
  const [selectedSpeed, setSelectedSpeed] = useState<'slow' | 'medium' | 'fast'>('medium');
  const [isLoadingPrivateKey, setIsLoadingPrivateKey] = useState(false);

  // Mutation for sending crypto
  const sendCryptoMutation = useSendCrypto();

  // Function to retrieve private key without showing notifications
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const retrievePrivateKey = useCallback(async (address: string) => {
    try {
      setIsLoadingPrivateKey(true);
      console.log('Retrieving private key for address:', address);

      const privateKey = await getPrivateKeyByAddress(address);
      console.log('Private key retrieved:', privateKey ? 'Yes (hidden)' : 'No');

      if (privateKey) {
        console.log('Setting private key in form');
        // Set the value directly in the form
        form.setFieldsValue({ private_key: privateKey });

        // Double-check if the value was set
        const formValues = form.getFieldsValue();
        console.log('Form values after setting private key:', {
          hasPrivateKey: !!formValues.private_key,
          privateKeyLength: formValues.private_key ? formValues.private_key.length : 0
        });
      } else {
        console.log('No private key returned from API');
      }
    } catch (error) {
      console.error('Error retrieving private key:', error);
      // No notification shown, just log the error
    } finally {
      setIsLoadingPrivateKey(false);
    }
  }, [form]);

  // Reset form when modal becomes visible or wallet data changes
  useEffect(() => {
    if (visible && walletData) {
      form.resetFields();
      form.setFieldsValue({
        from_address: walletData.address,
        coin: walletData.coin,
        network: walletData.network,
        user_id: walletData.user_id,
        speed: 'medium'
      });
      setFeeEstimation(null);
      setFeeEstimationError(null);
      setSelectedSpeed('medium');

      // Reset last estimation inputs
      setLastEstimationInputs({
        amount: '',
        to_address: '',
        token: '',
        speed: 'medium',
        timestamp: 0
      });

      // Automatically retrieve private key when modal opens
      if (walletData.address) {
        retrievePrivateKey(walletData.address);
      }
    }
  }, [visible, walletData, form, retrievePrivateKey]);

  // Trigger fee estimation when both amount and to_address are filled
  useEffect(() => {
    // Check if modal is visible and form is initialized
    if (visible && form) {
      const amount = form.getFieldValue('amount');
      const toAddress = form.getFieldValue('to_address');

      // If both fields are filled, trigger fee estimation
      if (amount && toAddress) {
        // Use a small delay to avoid immediate API call when modal opens
        const timer = setTimeout(() => {
          setShouldEstimateFee(true);
        }, 500);

        return () => clearTimeout(timer);
      }
    }
  }, [visible, form]);

  // Track if we should estimate fee
  const [shouldEstimateFee, setShouldEstimateFee] = useState(false);

  // Track last estimation inputs to prevent duplicate calls
  const [lastEstimationInputs, setLastEstimationInputs] = useState({
    amount: '',
    to_address: '',
    token: '',
    speed: selectedSpeed,
    timestamp: 0
  });

  // Set up field value listeners with debounce
  const handleAmountChange = (value: any) => {
    if (value && form.getFieldValue('to_address')) {
      setShouldEstimateFee(true);
    }
  };

  const handleToAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value && form.getFieldValue('amount')) {
      setShouldEstimateFee(true);
    }
  };

  const handleTokenChange = (_: React.ChangeEvent<HTMLInputElement>) => {
    if (form.getFieldValue('amount') && form.getFieldValue('to_address')) {
      setShouldEstimateFee(true);
    }
  };

  // Function to manually trigger fee estimation
  const triggerFeeEstimation = () => {
    setShouldEstimateFee(true);
  };

  // Estimate fee when needed
  useEffect(() => {
    if (!shouldEstimateFee) {
      return;
    }

    const fetchFeeEstimation = async () => {
      const amount = form.getFieldValue('amount');
      const toAddress = form.getFieldValue('to_address');
      const fromAddress = form.getFieldValue('from_address');
      const network = form.getFieldValue('network');
      const token = form.getFieldValue('token');

      // Skip if required fields are missing
      if (!amount || !toAddress || !fromAddress || !network) {
        setShouldEstimateFee(false);
        return;
      }

      // Check if inputs are the same as last estimation to prevent duplicate calls
      const currentInputs = {
        amount: amount.toString(),
        to_address: toAddress,
        token: token || '',
        speed: selectedSpeed,
        timestamp: Date.now()
      };

      // Skip if inputs haven't changed (except timestamp) and it's not a manual refresh
      if (
        lastEstimationInputs.amount === currentInputs.amount &&
        lastEstimationInputs.to_address === currentInputs.to_address &&
        lastEstimationInputs.token === currentInputs.token &&
        lastEstimationInputs.speed === currentInputs.speed &&
        currentInputs.timestamp - lastEstimationInputs.timestamp < 1000 // Allow manual refresh if more than 1 second has passed
      ) {
        setShouldEstimateFee(false);
        return;
      }

      // Update last estimation inputs
      setLastEstimationInputs(currentInputs);

      try {
        setIsEstimatingFee(true);
        setFeeEstimationError(null);

        // Set a timeout to prevent hanging
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error(t('sendTransaction.feeEstimationTimeout', 'Fee estimation timed out'))), 15000);
        });

        // Call fee estimation API using the service
        const fetchPromise = estimateFee(
          network,
          fromAddress,
          toAddress,
          amount.toString(),
          selectedSpeed,
          token
        );

        // Race between the fetch and the timeout
        const result = await Promise.race([fetchPromise, timeoutPromise]);

        if (!(result.success === true || result.status === true)) {
          throw new Error(result.error || t('sendTransaction.failedToEstimateFee', 'Failed to estimate fee'));
        }

        // Extract fee data from response
        const feeData = result.data || result;
        setFeeEstimation(feeData);
      } catch (error) {
        console.error('Error estimating fee:', error);
        setFeeEstimationError(error instanceof Error ? error.message : t('sendTransaction.unknownError', 'Unknown error'));
      } finally {
        setIsEstimatingFee(false);
        setShouldEstimateFee(false);
      }
    };

    // Use a small timeout to debounce the API call
    const debouncedEstimate = setTimeout(fetchFeeEstimation, 300);
    return () => clearTimeout(debouncedEstimate);
  }, [
    form,
    selectedSpeed,
    t,
    shouldEstimateFee,
    lastEstimationInputs.amount,
    lastEstimationInputs.to_address,
    lastEstimationInputs.token,
    lastEstimationInputs.speed,
    lastEstimationInputs.timestamp
    // estimateFee is intentionally omitted as it's an imported function that doesn't change
  ]);

  // Handle form submission
  const handleSubmit = async (values: any) => {
    try {
      // Validate required fields
      if (!values.from_address) {
        showErrorNotification(
          t('sendTransaction.missingFromAddress', 'Missing from address'),
          t('sendTransaction.fromAddressRequired', 'From address is required')
        );
        return;
      }

      if (!values.to_address) {
        showErrorNotification(
          t('sendTransaction.missingToAddress', 'Missing to address'),
          t('sendTransaction.toAddressRequired', 'To address is required')
        );
        return;
      }

      if (!values.amount) {
        showErrorNotification(
          t('sendTransaction.missingAmount', 'Missing amount'),
          t('sendTransaction.amountRequired', 'Amount is required')
        );
        return;
      }

      if (!values.network) {
        showErrorNotification(
          t('sendTransaction.missingNetwork', 'Missing network'),
          t('sendTransaction.networkRequired', 'Network is required')
        );
        return;
      }

      if (!values.coin) {
        showErrorNotification(
          t('sendTransaction.missingCoin', 'Missing coin'),
          t('sendTransaction.coinRequired', 'Coin is required')
        );
        return;
      }

      if (!values.private_key) {
        showErrorNotification(
          t('sendTransaction.missingPrivateKey', 'Missing private key'),
          t('sendTransaction.privateKeyRequired', 'Private key is required')
        );
        return;
      }

      // Format amount properly
      // For EVM chains, ensure the amount is a valid number with proper decimal places
      let formattedAmount = values.amount.toString();

      // Remove any commas that might be in the amount
      formattedAmount = formattedAmount.replace(/,/g, '');

      // Ensure it's a valid number
      if (isNaN(Number(formattedAmount))) {
        showErrorNotification(
          t('sendTransaction.invalidAmount', 'Invalid amount'),
          t('sendTransaction.amountMustBeNumber', 'Amount must be a valid number')
        );
        return;
      }

      // For very small numbers, ensure they're properly formatted
      // Convert scientific notation to decimal string if needed
      if (formattedAmount.includes('e')) {
        const num = Number(formattedAmount);
        formattedAmount = num.toFixed(18).replace(/\.?0+$/, '');
      }

      // Fix for BigNumber conversion issues:
      // Ensure the decimal string is properly formatted for BigNumber
      // This prevents errors like "invalid BigNumber string"
      if (formattedAmount.includes('.')) {
        // Parse to float and format with limited precision to avoid very long decimal values
        // that might cause BigNumber conversion issues
        const floatValue = parseFloat(formattedAmount);

        // Always use a fixed number of decimal places (6 is safe for most blockchains)
        // This ensures consistent formatting and prevents BigNumber parsing errors
        formattedAmount = floatValue.toFixed(6);
      }

      console.log('Sending transaction with amount:', formattedAmount);

      // Prepare data for API call
      const sendData = {
        provider: values.network,
        token: values.token || '',
        amount: formattedAmount,
        to: values.to_address,
        privatekey: values.private_key,
        from: values.from_address,
        is_evm: walletData?.is_evm || 1,
        tag: values.tag,
        publicKey: values.from_address,
        speed: values.speed || 'medium'
      };

      // Send transaction
      const response = await sendCryptoMutation.mutateAsync(sendData);

      console.log('Transaction response:', response);

      // Check if the response indicates success
      if (response && response.status === true) {
        showSuccessNotification(
          t('sendTransaction.success', 'Transaction sent'),
          t('sendTransaction.successMessage', 'Your transaction has been broadcast to the network')
        );
        onCancel();
      } else {
        // Handle error response
        const errorMessage = response?.error?.message ||
                            response?.error?.details?.originalMessage ||
                            response?.data ||
                            t('sendTransaction.unknownError', 'Unknown error');

        showErrorNotification(
          t('sendTransaction.error', 'Failed to send transaction'),
          errorMessage
        );
      }
    } catch (error) {
      console.error('Error sending transaction:', error);
      showErrorNotification(
        t('sendTransaction.error', 'Failed to send transaction'),
        error instanceof Error ? error.message : 'Unknown error'
      );
    }
  };

  // Handle speed change
  const handleSpeedChange = (value: 'slow' | 'medium' | 'fast') => {
    setSelectedSpeed(value);
    form.setFieldsValue({ speed: value });

    // Trigger fee estimation if we have the required fields
    if (form.getFieldValue('amount') && form.getFieldValue('to_address')) {
      setShouldEstimateFee(true);
    }
  };

  // Handle manual retrieval of private key (with notifications)
  const handleGetPrivateKey = async () => {
    if (!walletData?.address) {
      showErrorNotification(
        t('sendTransaction.error', 'Error'),
        t('sendTransaction.noWalletAddress', 'No wallet address available')
      );
      return;
    }

    try {
      setIsLoadingPrivateKey(true);
      console.log('Manual retrieval of private key for address:', walletData.address);

      const privateKey = await getPrivateKeyByAddress(walletData.address);
      console.log('Manual retrieval result:', privateKey ? 'Success' : 'Failed');

      if (privateKey) {
        // Set the value directly in the form
        form.setFieldsValue({ private_key: privateKey });

        // Force a form update to ensure the UI reflects the change
        setTimeout(() => {
          const formValues = form.getFieldsValue();
          console.log('Form values after manual retrieval:', {
            hasPrivateKey: !!formValues.private_key,
            privateKeyLength: formValues.private_key ? formValues.private_key.length : 0
          });
        }, 100);

        showSuccessNotification(
          t('sendTransaction.success', 'Success'),
          t('sendTransaction.privateKeyRetrieved', 'Private key retrieved successfully')
        );
      } else {
        showErrorNotification(
          t('sendTransaction.error', 'Error'),
          t('sendTransaction.privateKeyNotFound', 'Private key not found')
        );
      }
    } catch (error) {
      console.error('Error retrieving private key:', error);
      showErrorNotification(
        t('sendTransaction.error', 'Error'),
        error instanceof Error ? error.message : t('sendTransaction.unknownError', 'Unknown error')
      );
    } finally {
      setIsLoadingPrivateKey(false);
    }
  };

  return (
    <Modal
      title={
        <div>
          <SendOutlined style={{ marginRight: 8 }} />
          {t('sendTransaction.title', 'Send Transaction')}
        </div>
      }
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          from_address: walletData?.address || '',
          coin: walletData?.coin || '',
          network: walletData?.network || '',
          user_id: walletData?.user_id || '',
          speed: 'medium'
        }}
      >
        <Form.Item
          name="from_address"
          label={t('sendTransaction.fromAddress', 'From Address')}
          rules={[{ required: true, message: t('sendTransaction.fromAddressRequired', 'From address is required') }]}
        >
          <Input disabled />
        </Form.Item>

        <Form.Item
          name="to_address"
          label={t('sendTransaction.toAddress', 'To Address')}
          rules={[{ required: true, message: t('sendTransaction.toAddressRequired', 'To address is required') }]}
        >
          <Input
            placeholder={t('sendTransaction.toAddressPlaceholder', 'Enter destination address')}
            onChange={handleToAddressChange}
          />
        </Form.Item>

        <Form.Item
          name="amount"
          label={t('sendTransaction.amount', 'Amount')}
          rules={[{ required: true, message: t('sendTransaction.amountRequired', 'Amount is required') }]}
        >
          <InputNumber
            style={{ width: '100%' }}
            placeholder={t('sendTransaction.amountPlaceholder', 'Enter amount to send')}
            min={0}
            step={0.000001}
            stringMode
            onChange={handleAmountChange}
          />
        </Form.Item>

        <Form.Item
          name="tag"
          label={
            <span>
              {t('sendTransaction.tag', 'Tag/Memo')}
              <Tooltip title={t('sendTransaction.tagTooltip', 'Required for some networks like XRP, XLM, etc.')}>
                <InfoCircleOutlined style={{ marginLeft: 8 }} />
              </Tooltip>
            </span>
          }
        >
          <Input placeholder={t('sendTransaction.tagPlaceholder', 'Optional - Enter tag or memo if required')} />
        </Form.Item>

        <Form.Item
          name="network"
          label={t('sendTransaction.network', 'Network')}
          rules={[{ required: true, message: t('sendTransaction.networkRequired', 'Network is required') }]}
        >
          <Input disabled />
        </Form.Item>

        <Form.Item
          name="coin"
          label={t('sendTransaction.coin', 'Coin')}
          rules={[{ required: true, message: t('sendTransaction.coinRequired', 'Coin is required') }]}
        >
          <Input disabled />
        </Form.Item>

        <Form.Item
          name="token"
          label={
            <span>
              {t('sendTransaction.token', 'Token Contract (Optional)')}
              <Tooltip title={t('sendTransaction.tokenTooltip', 'Leave empty for native tokens')}>
                <InfoCircleOutlined style={{ marginLeft: 8 }} />
              </Tooltip>
            </span>
          }
        >
          <Input
            placeholder={t('sendTransaction.tokenPlaceholder', 'Enter token contract address if sending a token')}
            onChange={handleTokenChange}
          />
        </Form.Item>

        <Form.Item
          name="speed"
          label={t('sendTransaction.transactionSpeed', 'Transaction Speed')}
        >
          <Card className="fee-speed-card">
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Radio.Group
                  value={selectedSpeed}
                  onChange={(e) => handleSpeedChange(e.target.value)}
                  style={{ width: '100%' }}
                >
                  <Row gutter={[16, 16]}>
                    <Col span={8}>
                      <Radio.Button value="slow" style={{ width: '100%', height: '100%', textAlign: 'center', padding: '8px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <ClockCircleOutlined style={{ fontSize: '20px', marginBottom: '4px' }} />
                          <div>{t('sendTransaction.slow', 'Slow')}</div>
                          <div style={{ fontSize: '12px', color: '#888' }}>{t('sendTransaction.cheapest', 'Cheapest')}</div>
                        </div>
                      </Radio.Button>
                    </Col>
                    <Col span={8}>
                      <Radio.Button value="medium" style={{ width: '100%', height: '100%', textAlign: 'center', padding: '8px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <SendOutlined style={{ fontSize: '20px', marginBottom: '4px' }} />
                          <div>{t('sendTransaction.medium', 'Medium')}</div>
                          <div style={{ fontSize: '12px', color: '#888' }}>{t('sendTransaction.recommended', 'Recommended')}</div>
                        </div>
                      </Radio.Button>
                    </Col>
                    <Col span={8}>
                      <Radio.Button value="fast" style={{ width: '100%', height: '100%', textAlign: 'center', padding: '8px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <RocketOutlined style={{ fontSize: '20px', marginBottom: '4px' }} />
                          <div>{t('sendTransaction.fast', 'Fast')}</div>
                          <div style={{ fontSize: '12px', color: '#888' }}>{t('sendTransaction.fastest', 'Fastest')}</div>
                        </div>
                      </Radio.Button>
                    </Col>
                  </Row>
                </Radio.Group>
              </Col>
            </Row>
          </Card>
        </Form.Item>

        {/* Fee Estimation Section */}
        <div style={{ marginBottom: 16 }}>
          <Divider>
            <Space>
              <Text strong>{t('sendTransaction.feeEstimation', 'Fee Estimation')}</Text>
              <Button
                type="text"
                icon={<ReloadOutlined />}
                size="small"
                onClick={triggerFeeEstimation}
                title={t('sendTransaction.refreshFee', 'Refresh fee estimation')}
                disabled={isEstimatingFee || !form.getFieldValue('amount') || !form.getFieldValue('to_address')}
              />
            </Space>
          </Divider>

          {isEstimatingFee ? (
            <div style={{ textAlign: 'center', padding: '16px' }}>
              <Spin size="default" />
              <div style={{ marginTop: 8 }}>
                {t('sendTransaction.estimatingFee', 'Estimating transaction fee...')}
              </div>
            </div>
          ) : feeEstimationError ? (
            <Alert
              message={t('sendTransaction.feeEstimationError', 'Fee Estimation Error')}
              description={feeEstimationError}
              type="warning"
              showIcon
              style={{ marginTop: 8, marginBottom: 16 }}
            />
          ) : feeEstimation ? (
            <Card className="fee-estimation-card" style={{ marginTop: 8, marginBottom: 16 }}>
              <Row gutter={[16, 8]}>
                <Col span={12}>
                  <Text strong>{t('sendTransaction.estimatedFee', 'Estimated Fee')}:</Text>
                </Col>
                <Col span={12} style={{ textAlign: 'right' }}>
                  <Text>{feeEstimation.fee || feeEstimation.data?.fee || 'N/A'}</Text>
                </Col>

                {(feeEstimation.feeInUSD || feeEstimation.data?.feeInUSD) && (
                  <>
                    <Col span={12}>
                      <Text>{t('sendTransaction.feeInUSD', 'Fee in USD')}:</Text>
                    </Col>
                    <Col span={12} style={{ textAlign: 'right' }}>
                      <Text style={{ color: 'green' }}>
                        ${feeEstimation.feeInUSD || feeEstimation.data?.feeInUSD}
                      </Text>
                    </Col>
                  </>
                )}

                {(feeEstimation.feeInIRT || feeEstimation.data?.feeInIRT) && (
                  <>
                    <Col span={12}>
                      <Text>{t('sendTransaction.feeInIRT', 'Fee in IRT')}:</Text>
                    </Col>
                    <Col span={12} style={{ textAlign: 'right' }}>
                      <Text style={{ color: 'blue' }}>
                        {Number(feeEstimation.feeInIRT || feeEstimation.data?.feeInIRT).toLocaleString()} IRT
                      </Text>
                    </Col>
                  </>
                )}

                {(feeEstimation.gasPrice || feeEstimation.data?.gasPrice) && (
                  <>
                    <Col span={12}>
                      <Text>{t('sendTransaction.gasPrice', 'Gas Price')}:</Text>
                    </Col>
                    <Col span={12} style={{ textAlign: 'right' }}>
                      <Text>{feeEstimation.gasPrice || feeEstimation.data?.gasPrice}</Text>
                    </Col>
                  </>
                )}

                {(feeEstimation.gasLimit || feeEstimation.data?.gasLimit) && (
                  <>
                    <Col span={12}>
                      <Text>{t('sendTransaction.gasLimit', 'Gas Limit')}:</Text>
                    </Col>
                    <Col span={12} style={{ textAlign: 'right' }}>
                      <Text>{feeEstimation.gasLimit || feeEstimation.data?.gasLimit}</Text>
                    </Col>
                  </>
                )}

                {/* Additional fee details */}
                {(feeEstimation.details || feeEstimation.data?.details) && (
                  <>
                    <Col span={24}>
                      <Divider style={{ margin: '8px 0' }} />
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {t('sendTransaction.feeDetails', 'Fee Details')}
                      </Text>
                    </Col>

                    {(feeEstimation.details?.network || feeEstimation.data?.details?.network) && (
                      <>
                        <Col span={12}>
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            {t('sendTransaction.network', 'Network')}:
                          </Text>
                        </Col>
                        <Col span={12} style={{ textAlign: 'right' }}>
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            {feeEstimation.details?.network || feeEstimation.data?.details?.network}
                          </Text>
                        </Col>
                      </>
                    )}

                    {(feeEstimation.source || feeEstimation.data?.source) && (
                      <>
                        <Col span={12}>
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            {t('sendTransaction.source', 'Source')}:
                          </Text>
                        </Col>
                        <Col span={12} style={{ textAlign: 'right' }}>
                          <Tag color={
                            (feeEstimation.source === 'rpc' || feeEstimation.data?.source === 'rpc')
                              ? 'green'
                              : (feeEstimation.source === 'cache' || feeEstimation.data?.source === 'cache')
                                ? 'blue'
                                : 'orange'
                          }>
                            {feeEstimation.source || feeEstimation.data?.source || 'unknown'}
                          </Tag>
                        </Col>
                      </>
                    )}
                  </>
                )}
              </Row>
            </Card>
          ) : (
            <Alert
              message={t('sendTransaction.noFeeEstimation', 'No Fee Estimation')}
              description={t('sendTransaction.fillFieldsForFee', 'Fill in the amount and destination address to see fee estimation')}
              type="info"
              showIcon
              style={{ marginTop: 8, marginBottom: 16 }}
            />
          )}
        </div>

        <Form.Item
          name="private_key"
          label={
            <span>
              {t('sendTransaction.privateKey', 'Private Key')}
              <Tooltip title={t('sendTransaction.privateKeyTooltip', 'Your private key is required to sign the transaction')}>
                <InfoCircleOutlined style={{ marginLeft: 8 }} />
              </Tooltip>
            </span>
          }
          rules={[{ required: true, message: t('sendTransaction.privateKeyRequired', 'Private key is required') }]}
          extra={
            <div style={{ marginTop: 4 }}>
              <Button
                type="text"
                size="small"
                icon={<KeyOutlined />}
                onClick={handleGetPrivateKey}
                loading={isLoadingPrivateKey}
                style={{ padding: '0', height: 'auto' }}
              >
                {t('sendTransaction.refreshPrivateKey', 'Refresh Private Key')}
              </Button>
              <span style={{ fontSize: '12px', color: '#888', marginLeft: '8px' }}>
                {isLoadingPrivateKey
                  ? t('sendTransaction.loadingPrivateKey', 'Loading private key...')
                  : t('sendTransaction.privateKeyAutoLoaded', 'Private key is automatically loaded')}
              </span>
            </div>
          }
        >
          <div className="custom-password-input">
            <Form.Item name="private_key" noStyle>
              <Input
                placeholder={t('sendTransaction.privateKeyPlaceholder', 'Enter wallet private key')}
                type={privateKeyVisible ? 'text' : 'password'}
                autoComplete="new-password" // This prevents Chrome from auto-filling
                name="private_key_no_autofill" // Using a non-standard name helps prevent auto-fill
                suffix={
                  <Button
                    type="text"
                    icon={privateKeyVisible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                    onClick={() => setPrivateKeyVisible(!privateKeyVisible)}
                    style={{ border: 'none', padding: 0 }}
                  />
                }
              />
            </Form.Item>
          </div>
        </Form.Item>

        <Alert
          message={t('sendTransaction.securityWarning', 'Security Warning')}
          description={t('sendTransaction.securityWarningText', 'Never share your private key with anyone. This transaction is performed locally and your private key is not stored.')}
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Form.Item style={{ marginBottom: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Space>
              <Button onClick={onCancel}>
                {t('cancel', 'Cancel')}
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SendOutlined />}
                loading={sendCryptoMutation.isPending}
              >
                {t('sendTransaction.send', 'Send')}
              </Button>
            </Space>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default SendTransactionModal;
